/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import type { MutatorTx } from "../schema"

// Shared builder for the terminal-status update on a `HelpCenterArticleProposal`
// or `HelpCenterProposalGroup` row. Both tables carry the same `closedByUserId`/
// `closedAt`/`updatedAt`/`status` fields, so the same helper lays out either
// shape. Callers spread the result into the mutator's `update` payload alongside
// the row id.

export type TerminalProposalStatus = "ACCEPTED" | "REJECTED" | "SUPERSEDED"

export type CloseProposalArgs = {
	status: TerminalProposalStatus
	userId: string
	now: number
	// Set on EDIT-accept to record which version row materialized the apply.
	// Audit-only; ignored when present on groups (column doesn't exist there).
	appliedVersionId?: string
}

export function closeProposalAs(args: CloseProposalArgs) {
	return {
		status: args.status,
		closedByUserId: args.userId,
		closedAt: args.now,
		updatedAt: args.now,
		appliedVersionId: args.appliedVersionId,
	}
}

// A group the reviewer turned down used to close as ACCEPTED, so the docs agent
// never saw the decline and suggested the same topic again.
export function groupStatusFromChildren(
	statuses: readonly string[],
): TerminalProposalStatus {
	if (statuses.includes("ACCEPTED")) return "ACCEPTED"
	if (statuses.includes("REJECTED")) return "REJECTED"
	return "SUPERSEDED"
}

// After any child proposal reaches a terminal status, close its parent group
// if no OPEN/DRAFT siblings remain. Keeps the inbox tidy when reviewers accept
// or reject children one by one instead of using the whole-group actions.
export async function closeParentGroupIfAllTerminal(
	tx: MutatorTx,
	groupId: string,
	userId: string,
	now: number,
) {
	const siblings = await tx.query.helpCenterArticleProposals
		.where("proposalGroupId", "=", groupId)
		.run()
	const stillOpen = siblings.filter(
		(s) => s.status === "OPEN" || s.status === "DRAFT",
	)
	if (stillOpen.length > 0) return

	const [group] = await tx.query.helpCenterProposalGroups
		.where("id", "=", groupId)
		.run()
	if (!group || group.status !== "OPEN") return

	await tx.mutate.helpCenterProposalGroups.update({
		id: groupId,
		...closeProposalAs({
			status: groupStatusFromChildren(siblings.map((s) => s.status)),
			userId,
			now,
		}),
	})
}

// A suggestion on a deleted article can't be opened or reviewed, so deleting
// the article retires its open and draft suggestions.
export async function supersedeProposalsForArticles(
	tx: MutatorTx,
	articleIds: readonly string[],
	userId: string,
	now: number,
) {
	const groupIds = new Set<string>()
	for (const articleId of articleIds) {
		const proposals = await tx.query.helpCenterArticleProposals
			.where("articleId", "=", articleId)
			.run()
		for (const proposal of proposals) {
			if (proposal.status !== "OPEN" && proposal.status !== "DRAFT") continue
			await tx.mutate.helpCenterArticleProposals.update({
				id: proposal.id,
				...closeProposalAs({ status: "SUPERSEDED", userId, now }),
			})
			if (proposal.proposalGroupId) groupIds.add(proposal.proposalGroupId)
		}
	}
	for (const groupId of groupIds) {
		await closeParentGroupIfAllTerminal(tx, groupId, userId, now)
	}
}
